import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { useEffect, useState } from "react";
import BookingApi from "../../API/Apis/BookingApi";
import BookingDetalisListSideNavigation from "../../components/Layouts/ReservationsCard/BookingDetalisListSideNavigation";
import NotAuthorisedPage from "../notAuthorisedPage";
import DefaultPage from "../../components/Layouts/DefaultPage";
import BookingGetters from "../../store/onlineBooking/getters";
import theme, { formatThemeForReservedArea } from "../../config/MuiColorThemes";
import { alpha, createTheme, Theme, ThemeProvider } from "@mui/material/styles";
import {
  Avatar,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  Grid,
  InputAdornment,
  MenuItem,
  Pagination,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { TableSkeleton } from "../../components/UI/TableSkeleton/TableSkeleton";
import { ListItemSkeletonGroup } from "../../components/UI/ListItemSkeleton/ListItemSkeleton";
import CollapsibleNavbar from "../../components/Layouts/ReservedAreaDashboard/CollapseNavbar";
import { useTranslation } from "react-i18next";
import i18n from "../../config/i18n";
import { Search } from "@mui/icons-material";

export default function CustomerDetailsList() {
  const locationLang = i18n.language;
  const { t } = useTranslation();
  const [page, setPage] = React.useState(0);
  const [rowsPerPage] = React.useState(8);
  const appSettings = BookingGetters.getAppSettings();
  const [dynamicTheme, setDynamicTheme] = useState<Theme | null>(null);
  const [loading, setLoading] = useState(true);
  const [data, setCustomerDetails] = useState<any[]>([]);
  const [allCustomerDetails, setAllCustomerDetails] = useState<any[]>([]);
  const [showCustomerDetails, setShowCustomerDetails] = useState(false);
  const [showSearchText, setShowSearchText] = useState<string>("");
  const [sortOrder, setSortOrder] = useState<string>("recent");

  const textResourceDetails =
    BookingGetters.getTextResourceDetailsBasedOnLang();
  const textResourceData =
    textResourceDetails?.[locationLang]?.flatMap((item) => item.data || []) ||
    [];

  useEffect(() => {
    async function fetchTheme() {
      const dynamicPalette = await formatThemeForReservedArea();
      const newTheme = createTheme(dynamicPalette);
      setDynamicTheme(newTheme);
    }
    fetchTheme();
  }, []);

  useEffect(() => {
    const showSearchTextData = textResourceData.find(
      (item) => item.key === "searchCustomerText"
    );
    setShowSearchText(showSearchTextData?.body || t("Search Customer"));
  }, [locationLang, textResourceData]);

  useEffect(() => {
    setShowCustomerDetails(!!appSettings?.reservedArea);
  }, [appSettings]);

  const [totalCustomers, setTotalCustomers] = useState(0);
  const [allSearchableCustomers, setAllSearchableCustomers] = useState<any[]>(
    []
  );

  useEffect(() => {
    setLoading(true);
    const id: any = localStorage.getItem("userID");
    const offset = page * rowsPerPage;
    BookingApi.getCustomerDeatilsByUserid(id, offset, rowsPerPage).then((e) => {
      setTotalCustomers(e.data.total);
      setCustomerDetails(e.data.customerList);
      setAllCustomerDetails(e.data.customerList);
      setLoading(false);
    });
  }, [page, rowsPerPage]);

  useEffect(() => {
    const id: any = localStorage.getItem("userID");
    BookingApi.getCustomerDeatilsByUserid(id, 0, 9999).then((res) => {
      setAllSearchableCustomers(res.data.customerList || []);
    });
  }, []);

  const isSmallScreen = window.innerWidth <= 768;
  const [userId] = useState(localStorage.getItem("userID"));
  const [searchCustomer, setSearchCustomer] = useState("");

  const istabletScreen = window.innerWidth > 768 && window.innerWidth < 1300;

  const handleSearchCustomer = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchCustomer(value);

    if (value.trim() !== "") {
      const searchValue = value.trim().toLowerCase();
      const source =
        allSearchableCustomers.length > 0
          ? allSearchableCustomers
          : allCustomerDetails;
      const filteredData = source.filter((customer) => {
        const fullName = `${customer.name || ""} ${
          customer.surname || ""
        }`.toLowerCase();
        const email = (customer.email || "").toLowerCase();
        return fullName.includes(searchValue) || email.includes(searchValue);
      });
      setCustomerDetails(filteredData);
    } else {
      setCustomerDetails(allCustomerDetails);
    }
  };

  const sortedData = React.useMemo(() => {
    if (sortOrder === "nameAsc") {
      return [...data].sort((a, b) =>
        `${a.name} ${a.surname}`.localeCompare(`${b.name} ${b.surname}`)
      );
    }
    if (sortOrder === "nameDesc") {
      return [...data].sort((a, b) =>
        `${b.name} ${b.surname}`.localeCompare(`${a.name} ${a.surname}`)
      );
    }
    return data;
  }, [data, sortOrder]);

  const primaryColor = dynamicTheme?.palette?.primary?.main || "#2cb133";
  const totalPages = Math.ceil(totalCustomers / rowsPerPage);
  const rangeStart = totalCustomers === 0 ? 0 : page * rowsPerPage + 1;
  const rangeEnd = Math.min((page + 1) * rowsPerPage, totalCustomers);

  const getInitials = (name: string, surname: string) =>
    `${name?.[0] || ""}${surname?.[0] || ""}`.toUpperCase();

  const handlePageChange = (_: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage - 1);
  };

  return (
    <ThemeProvider theme={dynamicTheme || theme}>
      <div>
        {showCustomerDetails ? (
          <div
            className="flex-row md:flex"
            style={{ backgroundColor: "#f5f5f5", minHeight: "100vh" }}
          >
            {/* Sidebar — desktop only */}
            {!isSmallScreen && !istabletScreen && (
              <div style={{ width: "260px", flexShrink: 0 }}>
                <BookingDetalisListSideNavigation />
              </div>
            )}

            {/* Collapsible navbar — mobile / tablet */}
            {(isSmallScreen || istabletScreen) && (
              <CollapsibleNavbar callFrom="showReservations" />
            )}

            {/* Main content */}
            <div
              style={{
                flex: 1,
                padding: isSmallScreen ? "20px 16px" : "10px 100px",
                minWidth: 0,
              }}
            >
              {/* Title */}
              <p
                style={{
                  fontSize: "26px",
                  fontWeight: 700,
                  color: "#222",
                  marginTop: "10px",
                  lineHeight: 1.2,
                }}
              >
                {t("Customer Details")}
              </p>
              <p
                style={{
                  fontSize: "14px",
                  color: "#9ca3af",
                  margin: "6px 0 20px",
                }}
              >
                {t("You have")} {totalCustomers} {t("contact in total")}
              </p>

              {/* Search + Sort row */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "20px",
                  width: "100%",
                  flexWrap: isSmallScreen ? "wrap" : "nowrap",
                  gap: isSmallScreen ? "12px" : "0px",
                }}
              >
                <TextField
                  placeholder={showSearchText}
                  variant="outlined"
                  value={searchCustomer}
                  onChange={handleSearchCustomer}
                  style={{
                    width: isSmallScreen ? "100%" : "50%",
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search style={{ color: "#aaa" }} />
                      </InputAdornment>
                    ),
                    style: {
                      height: "44px",
                      borderRadius: "10px",
                      backgroundColor: "#fff",
                      fontSize: "14px",
                    },
                  }}
                />
                <FormControl
                  style={{
                    width: "240px",
                    flexShrink: 0,
                  }}
                >
                  <Select
                    value={sortOrder}
                    onChange={(e) => setSortOrder(e.target.value)}
                    displayEmpty
                    style={{
                      height: "44px",
                      borderRadius: "10px",
                      backgroundColor: "#fff",
                      fontSize: "14px",
                    }}
                  >
                    <MenuItem value="recent">
                      {t("Sort by date (most recent)")}
                    </MenuItem>
                  </Select>
                </FormControl>
              </div>

              {/* Content */}
              {loading ? (
                isSmallScreen ? (
                  <ListItemSkeletonGroup />
                ) : (
                  <TableSkeleton columns={4} />
                )
              ) : sortedData.length === 0 ? (
                <DefaultPage
                  message={t(
                    "You have not created any booking yet. To create a new booking, please click on the Book a car button."
                  )}
                />
              ) : !isSmallScreen ? (
                <>
                  {/* Desktop table */}
                  <div
                    style={{
                      backgroundColor: "#fff",
                      borderRadius: "12px",
                      overflow: "hidden",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                    }}
                  >
                    <TableContainer>
                      <Table aria-label="customer details table" stickyHeader>
                        <TableHead>
                          <TableRow>
                            {[
                              "Full name",
                              "Email",
                              "Mobile",
                              "Company Name",
                            ].map((header, index) => (
                              <TableCell
                                key={index}
                                style={{
                                  backgroundColor: alpha(primaryColor, 0.1),
                                  color: primaryColor,
                                  fontWeight: 400,
                                  fontSize: "14px",
                                  borderBottom: "none",
                                }}
                              >
                                {t(header)}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {sortedData.map((e, i) => (
                            <TableRow
                              key={e.id || i}
                              sx={{
                                "&:hover": { backgroundColor: "#f9fafb" },
                                "&:last-child td": { borderBottom: 0 },
                              }}
                            >
                              <TableCell style={{ fontSize: "14px" }}>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "10px",
                                  }}
                                >
                                  <Avatar
                                    sx={{
                                      width: 34,
                                      height: 34,
                                      bgcolor: "#d1d5db",
                                      color: "#374151",
                                      fontSize: "12px",
                                      fontWeight: 600,
                                    }}
                                  >
                                    {getInitials(e.name, e.surname)}
                                  </Avatar>
                                  {e.name || ""} {e.surname || ""}
                                </div>
                              </TableCell>
                              <TableCell style={{ fontSize: "14px" }}>
                                {e.email || ""}
                              </TableCell>
                              <TableCell style={{ fontSize: "14px" }}>
                                {e.phoneNumb1 || ""}
                              </TableCell>
                              <TableCell style={{ fontSize: "14px" }}>
                                {e.isPhysicalPerson === "true"
                                  ? ""
                                  : e.companyName || ""}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </div>

                  {/* Pagination */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: "16px",
                      marginTop: "20px",
                    }}
                  >
                    <span style={{ fontSize: "13px", color: "#9ca3af" }}>
                      {rangeStart}–{rangeEnd} {t("of")} {totalCustomers}
                    </span>
                    <Pagination
                      count={totalPages}
                      page={page + 1}
                      onChange={handlePageChange}
                      color="primary"
                      shape="rounded"
                      size="small"
                      sx={{
                        "& .MuiPaginationItem-root.Mui-selected": {
                          backgroundColor: alpha(primaryColor, 0.1),
                          color: primaryColor,
                          fontWeight: 400,
                          "&:hover": {
                            backgroundColor: alpha(primaryColor, 0.18),
                          },
                        },
                        "& .MuiPaginationItem-root": {
                          "&:hover": {
                            backgroundColor: alpha(primaryColor, 0.06),
                          },
                        },
                      }}
                    />
                  </div>
                </>
              ) : (
                <>
                  {/* Mobile card view */}
                  <Grid container spacing={1.5}>
                    {sortedData.map((e, i) => (
                      <Grid item xs={12} sm={6} key={e.id || i}>
                        <Card style={{ borderRadius: "10px" }}>
                          <CardHeader
                            title={`${e.name || ""} ${e.surname || ""}`}
                            style={{
                              backgroundColor: alpha(primaryColor, 0.1),
                              height: "32px",
                              padding: "4px 12px",
                              borderRadius: "10px 10px 0 0",
                            }}
                            titleTypographyProps={{
                              style: {
                                color: primaryColor,
                                fontSize: "13px",
                                fontWeight: 400,
                              },
                            }}
                          />
                          <CardContent style={{ padding: "10px 12px" }}>
                            {[
                              {
                                label: "Full name",
                                value: `${e.name || ""} ${e.surname || ""}`,
                              },
                              { label: "Email", value: e.email || "" },
                              { label: "Mobile", value: e.phoneNumb1 || "" },
                              {
                                label: "Company Name",
                                value:
                                  e.isPhysicalPerson === "true"
                                    ? ""
                                    : e.companyName || "",
                              },
                            ].map((field, index) => (
                              <React.Fragment key={index}>
                                <Typography
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    fontSize: "13px",
                                    marginBottom: "4px",
                                  }}
                                >
                                  <span>{t(field.label)}</span>
                                  <span>{field.value}</span>
                                </Typography>
                                {index < 3 && <hr />}
                              </React.Fragment>
                            ))}
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: "16px",
                      marginTop: "20px",
                    }}
                  >
                    <span style={{ fontSize: "13px", color: "#9ca3af" }}>
                      {rangeStart}–{rangeEnd} {t("of")} {totalCustomers}
                    </span>
                    <Pagination
                      count={totalPages}
                      page={page + 1}
                      onChange={handlePageChange}
                      color="primary"
                      shape="rounded"
                      size="small"
                      sx={{
                        "& .MuiPaginationItem-root.Mui-selected": {
                          backgroundColor: alpha(primaryColor, 0.1),
                          color: primaryColor,
                          fontWeight: 400,
                          "&:hover": {
                            backgroundColor: alpha(primaryColor, 0.18),
                          },
                        },
                        "& .MuiPaginationItem-root": {
                          "&:hover": {
                            backgroundColor: alpha(primaryColor, 0.06),
                          },
                        },
                      }}
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        ) : (
          <NotAuthorisedPage />
        )}
      </div>
    </ThemeProvider>
  );
}
